/**
 * 授權申請的**批次核准**（批141，主人裁示 30／40）——勾選規則與結果整理。
 *
 * 後端 `POST /api/admin/license-requests/batch-approve`：照各筆**原內容**簽發、逐筆套核發
 * 政策、一筆一交易（失敗的那筆 rollback，成功的留著），上限 200 筆。
 *
 * 這裡的判斷**只是不給勾明知會失敗的列**，真正的判定在後端（每筆重新 `FOR UPDATE`）：
 * - 只有 `pending`（草稿與已審結的不能批次）；
 * - **代管開站申請一律排除**——它要選主機並開站，必須逐筆走「核可並開站」。
 */
import type { BatchApproveResult, LicenseReq } from '@/api/ls'
import { BATCH_APPROVE_MAX } from '@/api/ls'

export { BATCH_APPROVE_MAX }

/** 這一列可不可以勾進批次核准。 */
export function batchEligible(r: Pick<LicenseReq, 'status' | 'kind'>): boolean {
  return r.status === 'pending' && r.kind !== 'hosted_provision'
}

/** 不能勾的原因（`null`＝可以勾）——滑過勾選框時顯示。 */
export function batchIneligibleReason(r: Pick<LicenseReq, 'status' | 'kind'>): string | null {
  if (r.status !== 'pending') return '只有待審核的申請可以批次核准'
  if (r.kind === 'hosted_provision') return '代管開站申請要選主機並開站，請逐筆用「核可並開站」處理'
  return null
}

/**
 * 清單重載之後，把已經不在畫面上（或不再可勾）的勾選拿掉——
 * 換籤、載入更多、別人剛處理掉某一筆，都不該留下看不見的勾選被一起送出去。
 */
export function pruneSelection(selected: readonly string[], rows: readonly LicenseReq[]): string[] {
  const ok = new Set(rows.filter(batchEligible).map((r) => r.id))
  return selected.filter((id) => ok.has(id))
}

/** 切換一列的勾選（保序、不重複）。 */
export function toggleSelection(selected: readonly string[], id: string): string[] {
  return selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]
}

/** 「全選」：這一頁可勾的列全部勾上；已經全勾了就全部取消。 */
export function toggleAll(selected: readonly string[], rows: readonly LicenseReq[]): string[] {
  const ids = rows.filter(batchEligible).map((r) => r.id)
  const all = ids.length > 0 && ids.every((id) => selected.includes(id))
  return all ? selected.filter((id) => !ids.includes(id)) : [...new Set([...selected, ...ids])]
}

/** 失敗原因碼的中文（後端的 `message` 優先；這張表只在 message 空白時接手）。 */
export const BATCH_FAILURE_LABELS: Record<string, string> = {
  not_found: '查無這筆申請',
  not_batchable: '不在待審核狀態（可能已被處理）',
  use_approve_hosted: '代管開站申請要逐筆用「核可並開站」處理',
  conflict: '與其他寫入衝突，請重新整理後再試',
  superadmin_required: '這筆的核發政策需要更高的身分',
  activation_requires_approval: '這筆的核發政策需要更高的身分',
  pending_exists: '同一台主機另有待審核的申請',
  hosted_instance: '指向代管站台，授權由中台派送',
}

export interface BatchResultRow {
  id: string
  /** 顯示用的客戶名（呼叫端從列上或對照表查）。 */
  customer: string
  ok: boolean
  /** 成功＝簽出的授權編號；失敗＝原因（中文）。 */
  text: string
  /** 失敗原因碼（成功為 null）。 */
  code: string | null
}

/**
 * 結果彈窗的列：**依送出的順序**，成功與失敗各自標好（後端回應的順序本來就是 ids 順序，
 * 這裡再依 `ids` 排一次，別依賴回應的排列）。
 */
export function batchResultRows(
  ids: readonly string[],
  result: BatchApproveResult,
  customerOf: (id: string) => string,
): BatchResultRow[] {
  const ok = new Map(result.approved.map((a) => [a.id, a]))
  const bad = new Map(result.failed.map((f) => [f.id, f]))
  const out: BatchResultRow[] = []
  for (const id of ids) {
    const a = ok.get(id)
    if (a) {
      out.push({
        id,
        customer: customerOf(id),
        ok: true,
        text: a.license_id ?? a.addon_id ?? '已核發',
        code: null,
      })
      continue
    }
    const f = bad.get(id)
    if (f) {
      out.push({
        id,
        customer: customerOf(id),
        ok: false,
        text: f.message || BATCH_FAILURE_LABELS[f.code] || f.code,
        code: f.code,
      })
    }
  }
  return out
}

/** 確認框的說明文字（對話框是純文字，不放 `**`）。 */
export function batchConfirmMessage(n: number): string {
  return (
    `即將照原申請內容核發 ${n} 筆授權申請。` +
    '每一筆各自套核發政策與內容檢查——需要更高身分或內容有問題的那幾筆會個別失敗，其餘照常核發；' +
    '結果會逐筆列出。代管開站申請不在其中（要逐筆用「核可並開站」處理）。確定要核准嗎？'
  )
}
